import {useMemo, useState} from 'react';
import {amount, type Work} from './api';
import {type DocumentDelivery} from './documentDelivery';
import {sourcePageLabel, useSourceLabels} from './SourcePicker';

export function SourceStructure({rows, revisionId, plan, pick}: {
  rows: Work[]; revisionId: string; plan: DocumentDelivery | undefined; pick: (id: string) => void;
}) {
  const [search, setSearch] = useState('');
  const ids = useMemo(() => [...new Set(rows.flatMap(row => row.source_ids))], [rows]);
  const {labels, error} = useSourceLabels(revisionId, ids);
  const index = new Map(labels.map(label => [label.id, label]));
  const assignment = new Map((plan?.assignments || []).map(item => [item.work_id, item]));
  const documents = new Map((plan?.documents || []).map(item => [item.document_id, item]));
  const ordered = [...rows].sort((a, b) => {
    const primary = (work: Work) => {
      const owner = documents.get(assignment.get(work.id)?.document_id || '');
      const source = work.source_ids.map(id => index.get(id)).find(label => label && owner?.path.endsWith(label.document));
      return [owner?.path || '\uffff', source?.sheet || '\uffff'].join('/');
    };
    return primary(a).localeCompare(primary(b), 'ru', {numeric: true}) || a.description.localeCompare(b.description, 'ru');
  }).filter(work => [work.description, work.building, work.category, work.group,
    ...work.source_ids.map(id => {const source = index.get(id); return source ? `${source.document} ${source.sheet}` : '';})]
    .join(' ').toLocaleLowerCase().includes(search.toLocaleLowerCase()));
  return <section className="source-structure">
    <p>Работы текущего вида сопоставлены с исходными PDF и страницами. Каждая работа показана один раз. Полный реестр дополнительных оснований находится в комплекте ZIP. Названия рабочих подразделов не заменяют разделы исходного проекта.</p>
    <label className="field"><span>Найти работу, PDF или страницу</span><input value={search} onChange={event => setSearch(event.target.value)}/></label>
    {error && <p role="alert">Не удалось прочитать названия источников. Повторно откройте сопоставление.</p>}
    <p className="muted">Работ: {ordered.length}. Объёмы по повторным ссылкам не суммируются.</p>
    <table><thead><tr><th>Исходный PDF / страницы</th><th>Рабочий подраздел / работа</th><th>Объём</th></tr></thead>
      <tbody>{ordered.map(work => {
        const owner=documents.get(assignment.get(work.id)?.document_id || '');
        const pages=new Map<string,Set<string>>();
        let pending=false;
        for(const id of work.source_ids){
          const source=index.get(id);
          if(!source){pending=true;continue;}
          if(!pages.has(source.document))pages.set(source.document,new Set());
          pages.get(source.document)!.add(sourcePageLabel(source.document,source.sheet,source.drawing_sheet));
        }
        return <tr key={work.id}>
        <td><strong>{owner?.label || 'Основной PDF не определён'}</strong>
          {[...pages].map(([document,addresses])=><small key={document}>{owner?.path.endsWith(document)?'':`${document} · `}{[...addresses].join('; ')}</small>)}
          {pending && <small>Читаем адрес источника…</small>}</td>
        <td><small>{[work.building,work.discipline,work.category,work.group].filter(Boolean).join(' / ')}</small><button className="quiet" onClick={() => pick(work.id)}>{work.description}</button></td>
        <td>{amount(work.quantity)} {work.unit}</td>
      </tr>;})}</tbody></table>
  </section>;
}
